import { css } from '@emotion/css';
import { Box, Grid } from '@mui/material';
import Skeleton from 'atoms/Skeleton';
import TaskCard from 'atoms/TaskCard';
import TaskModal from 'molecules/taskModal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadingEnd, loadingStart } from 'store/globalSlice';
import { RootState } from 'store/store';
import { setTasks } from 'store/taskSlice';
import { colors } from 'styles/colors';
import { TaskAPI, TaskType } from 'utils/TaskAPI';

const TimeLinePage = () => {
    const dispatch = useDispatch();

    const token = useSelector((state: RootState) => state.global.authToken);
    const tasks = useSelector((state: RootState) => state.task.tasks);
    const isLoading = useSelector((state: RootState) => state.global.isLoading);

    const [modalTask, setModalTask] = useState<TaskType | null>(null);
    const [openModal, setOpenModal] = useState(false);

    const getTasksData = useCallback(async () => {
        try {
            dispatch(loadingStart());
            const tasksData = await TaskAPI.getTasks(token ?? '');
            dispatch(setTasks(tasksData.tasks));
        } finally {
            dispatch(loadingEnd());
        }
    }, [dispatch, token]);

    const handleOpenModal = (taskForModal: TaskType) => {
        setModalTask(taskForModal);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setModalTask(null);
        setOpenModal(false);
    };

    useEffect(() => {
        getTasksData();
    }, [getTasksData]);

    const { uncompletedTasks, completedTasks } = useMemo(() => {
        const list = tasks ?? [];
        const sorted = [...list].sort((a, b) => new Date(a.dueOnDate).getTime() - new Date(b.dueOnDate).getTime());
        return {
            uncompletedTasks: sorted.filter((t) => !t.isCompleted),
            completedTasks: sorted.filter((t) => t.isCompleted),
        };
    }, [tasks]);

    return (
        <div className={styles.page}>
            <div className={styles.content}>
                <Skeleton loading={isLoading}>
                    <div className={styles.title}>Mine opgaver</div>
                </Skeleton>

                <Skeleton loading={isLoading}>
                    <div className={styles.underTitleText}>
                        Her ser du en oversigt over dine aktiviteter. For at udføre en aktivitet, skal du bruge
                        PlanetDiet App'en.
                    </div>
                </Skeleton>
            </div>
            <div className={styles.cardsContainer}>
                <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={5} columns={2}>
                        <Grid item xs={2} sm={2} md={1} lg={1} style={{ height: 'auto' }}>
                            <Skeleton loading={isLoading}>
                                <div className={styles.titleLists}>Igangværende og kommende aktiviteter</div>
                            </Skeleton>

                            <div className={styles.gridContainer} style={{ background: colors.backgroundLight }}>
                                <div className={styles.gridContent}>
                                    <div className={styles.cardContainer}>
                                        {(isLoading ? ([{}, {}, {}, {}, {}] as TaskType[]) : uncompletedTasks).map(
                                            (task, idx) => (
                                                <TaskCard
                                                    key={task.taskId || `${task.title}-${idx}`}
                                                    task={task}
                                                    onOpen={handleOpenModal}
                                                    isLast={idx === uncompletedTasks.length - 1}
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={2} sm={2} md={1} lg={1}>
                            <Skeleton loading={isLoading}>
                                <div className={styles.titleLists}>Afsluttede aktiviteter</div>
                            </Skeleton>

                            <div className={styles.gridContainer} style={{ background: !isLoading ? colors.accent : colors.backgroundLight }}>
                                <div className={styles.gridContent}>
                                    <div className={styles.cardContainer}>
                                        {(isLoading ? ([{}, {}, {}, {}, {}] as TaskType[]) : completedTasks).map(
                                            (task, idx) => (
                                                <TaskCard
                                                    key={task.taskId || `${task.title}-${idx}`}
                                                    task={task}
                                                    onOpen={handleOpenModal}
                                                    isLast={idx === completedTasks.length - 1}
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </Box>
            </div>
            <TaskModal task={modalTask} openModal={openModal} handleCloseModal={handleCloseModal} />
        </div>
    );
};

const styles = {
    page: css({
        height: 'calc(100% - 5.5rem)',
        position: 'absolute',
        marginTop: '5rem',
        marginBottom: '1rem',
        '.MuiGrid-root': {
            height: '100%',
        },
        marginLeft: '2rem',
        marginRight: '2rem',
        width: 'calc(100% - 4rem)',
        maxWidth: '65rem',
        '::-webkit-scrollbar-thumb': {
            borderRadius: '6px',
        },
    }),
    content: css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        marginTop: '2.38rem',
        paddingRight: '20px',
        marginBottom: '3rem',
    }),
    gridContainer: css({
        height: '100%',
        maxWidth: '31.25rem',
        margin: 'auto',
        filter: 'drop-shadow(0px 4px 4px rgba(53, 79, 82, 0.20)) !important',
        borderRadius: '1rem',
        maxHeight: '530px',
    }),
    gridContent: css({
        height: '100%',
        width: '100%',
        // Create outer spacing that is always visible (outside of scroll area)
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        paddingTop: '2.5rem',
        paddingBottom: '2.5rem',
        position: 'relative',
    }),
    title: css({
        color: colors.primaryText,
        fontFamily: 'Figtree',
        fontSize: '35px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '150%',
        letterSpacing: '-0.385px',
    }),
    underTitleText: css({
        color: colors.primaryText,
        fontFamily: 'Figtree',
        fontSize: '22px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '150%',
        maxWidth: '80rem',
        marginTop: '0.5rem',
        marginBottom: '1.5rem',
    }),
    cardsContainer: css({
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        height: 'calc(100% - 10rem)',
        position: 'relative',
    }),
    cardContainer: css({
        // Fill remaining space within gridContent and make it scrollable
        flex: '1 1 auto',
        // Ensure padding stays within the element's box to avoid overflow beyond container
        boxSizing: 'border-box',
        // Make the scroll area slightly narrower and keep it centered, creating a right gutter (moves scrollbar left)
        width: 'calc(100% - 1.5rem)',
        margin: '0 auto',
        // Keep scrollbar space stable to avoid content shift
        scrollbarGutter: 'stable',
        // Enable vertical scrolling when content exceeds the container height
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        // Custom scrollbar styles (WebKit-based browsers)
        '::-webkit-scrollbar': {
            width: '8px',
        },
        '::-webkit-scrollbar-track': {
            background: 'transparent',
            borderRadius: '8px',
        },
        '::-webkit-scrollbar-thumb': {
            backgroundColor: '#cfcfcf', // light grey
            borderRadius: '8px',
            border: '2px solid transparent',
            backgroundClip: 'content-box',
        },
        '::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#bdbdbd', // slightly darker on hover
        },
        // Firefox scrollbar styles
        scrollbarWidth: 'thin',
        scrollbarColor: '#cfcfcf transparent',
    }),
    titleLists: css({
        color: colors.primaryText,
        fontFamily: 'Figtree',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: 'normal',
        marginBottom: '1rem',
        paddingLeft: '1rem',
        paddingTop: '1rem',
    }),
};

export default TimeLinePage;
